"use client";

import * as React from "react";
import {
  motion,
  useScroll,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { storyLines } from "@/data/intelligence";

function Line({
  index,
  text,
  highlight,
  progress,
}: {
  index: number;
  text: string;
  highlight?: string;
  progress: MotionValue<number>;
}) {
  const start = 0.04 + index * 0.26;
  const opacity = useTransform(progress, [start, start + 0.16], [0.16, 1]);
  const y = useTransform(progress, [start, start + 0.16], [14, 0]);
  const blur = useTransform(progress, [start, start + 0.16], [6, 0]);
  const filter = useTransform(blur, (b) => `blur(${b}px)`);

  const parts = highlight ? text.split(highlight) : [text];

  return (
    <motion.li
      style={{ opacity, y, filter }}
      className="story-line flex items-baseline gap-4 sm:gap-6"
    >
      <span className="w-7 shrink-0 text-xs font-medium tabular-nums tracking-widest text-electric/80 sm:w-9 sm:text-sm">
        0{index + 1}
      </span>
      <span className="h-display text-[clamp(1.75rem,4.8vw,3.9rem)] text-fg">
        {highlight ? (
          <>
            {parts[0]}
            <span className="text-gradient">{highlight}</span>
            {parts[1]}
          </>
        ) : (
          text
        )}
      </span>
    </motion.li>
  );
}

/**
 * Storytelling 01 → 03. Cada frase "acende" conforme o scroll (Apple-like),
 * com a seção fixa (sticky). Com movimento reduzido (CSS @media em globals.css),
 * a seção vira um bloco comum e tudo aparece de uma vez.
 */
export default function StorySection() {
  const ref = React.useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  return (
    <section
      id="historia"
      ref={ref}
      aria-label="Nossa visão"
      className="story-section relative h-[230vh] max-sm:h-[210vh]"
    >
      <div className="story-sticky sticky top-0 flex h-screen items-center">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 50% at 30% 50%, rgb(var(--brand) / 0.10), transparent 70%)",
          }}
        />
        <div className="container-x relative">
          <ol className="max-w-5xl space-y-7 sm:space-y-10">
            {storyLines.map((l, i) => (
              <Line
                key={l.text}
                index={i}
                text={l.text}
                highlight={l.highlight}
                progress={scrollYProgress}
              />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
